var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { property } from 'lit/decorators.js';
// Mixins
import { BaseElement } from '@frontend_zz/base-element/BaseElement.js';
// Utils
import { getCookieList, getCookieValue, setCookie } from '@frontend_zz/web-component-utilities/cookieHelpers.js';
// Components
import { Button } from '@frontend_zz/button/Button.js';
import { Drawer } from '@frontend_zz/drawer/Drawer.js';
import { Text } from '@frontend_zz/text/Text.js';
// Style
import styles from './CookieBanner.css.js';
// Data
import content from './CookieBanner.content.json';
import config from './CookieBanner.config.json';
/**
 * @element zz-cookie-banner
 */
export class CookieBanner extends BaseElement {
    static get styles() {
        return [styles];
    }
    static get scopedElements() {
        return {
            'zz-button': Button,
            'zz-drawer': Drawer,
            'zz-text': Text,
        };
    }
    /**
     * Initialization tasks that must be done before the first update
     */
    constructor() {
        super();
        this.visible = false;
        this.href = content.anchor.href;
        /**
         * Set cookies to default value, without user consent
         */
        this.setGivenConsentToDefaults = () => {
            const { setGivenConsentCookieTypeList } = this;
            const { noConsentCookieTypeList, consentCookieTypeList } = config;
            /*
              Set all cookie types as a single cookie with NEGATIVE consent,
              overwriting previous POSITIVE consent
             */
            setGivenConsentCookieTypeList(consentCookieTypeList, false);
            // Set all cookie types as a single cookie with POSITIVE consent
            setGivenConsentCookieTypeList(noConsentCookieTypeList, true);
        };
        /**
         * Set each cookie type as a single cookie
         */
        this.setGivenConsentCookieTypeList = (consentToCookieTypeList, isConsentGiven) => {
            // Set all cookie types as a single cookie
            consentToCookieTypeList.forEach((cookieType) => {
                setCookie({
                    key: `consent.${cookieType}`,
                    value: String(isConsentGiven),
                });
            });
        };
        /**
         * Set user consent and version cookie and hide the cookie banner
         */
        this.setGivenConsentToCookies = ({ isConsentGiven, policyVersion, }) => {
            setCookie({
                key: 'user.consent',
                value: String(isConsentGiven),
            });
            setCookie({
                key: 'consent.version',
                value: policyVersion,
            });
            // Hide the cookie banner
            this.visible = false;
        };
        /**
         * Click handler for no consent
         */
        this.onClickNoConsent = () => {
            const { setGivenConsentToDefaults, setGivenConsentCookieTypeList, setGivenConsentToCookies, broadcastConsentChange, } = this;
            const { noConsentCookieTypeList } = config;
            const { policyVersion } = content;
            setGivenConsentToDefaults();
            setGivenConsentCookieTypeList(noConsentCookieTypeList, true);
            setGivenConsentToCookies({
                isConsentGiven: true,
                policyVersion,
            });
            broadcastConsentChange(noConsentCookieTypeList);
        };
        /**
         * Click handler for consent
         */
        this.onClickConsent = (_event) => {
            const { setGivenConsentCookieTypeList, setGivenConsentToCookies, broadcastConsentChange } = this;
            const { consentCookieTypeList } = config;
            const { policyVersion } = content;
            setGivenConsentCookieTypeList(consentCookieTypeList, true);
            setGivenConsentToCookies({
                isConsentGiven: true,
                policyVersion,
            });
            broadcastConsentChange(consentCookieTypeList);
        };
        /**
         * Broadcast interaction events
         */
        this.broadcastConsentChange = (cookieTypeList) => {
            this.dispatchEvent(new CustomEvent('consent-change', {
                detail: { cookieTypes: cookieTypeList },
            }));
        };
        this.broadcastDismiss = () => {
            this.visible = false;
            this.dispatchEvent(new Event('dismiss'));
        };
        const { setGivenConsentToDefaults } = this;
        // Check for consent
        if (!CookieBanner.hasUserConsent) {
            setGivenConsentToDefaults();
            setCookie({
                key: 'user.consent',
                value: String(false),
            });
            // Show the cookie banner
            this.visible = true;
        }
        // Check policy version mismatch
        if (CookieBanner.hasVersionMismatch) {
            // Show the cookie banner
            this.visible = true;
        }
    }
    /**
     * Has the user given consent?
     */
    static get hasUserConsent() {
        const cookieList = getCookieList(document.cookie);
        const userConsentValue = getCookieValue(cookieList, 'user.consent');
        if (userConsentValue) {
            return userConsentValue !== 'false';
        }
        return false;
    }
    /**
     * Has the user consented to an out of date policy?
     * TODO: consider using package.json version instead of policyVersion property from content
     */
    static get hasVersionMismatch() {
        const { policyVersion } = content;
        const cookieList = getCookieList(document.cookie);
        const consentVersionValue = getCookieValue(cookieList, 'consent.version');
        if (consentVersionValue) {
            const [consentVersionYear, consentVersionIncrement] = consentVersionValue.split('.');
            const [versionYear, versionIncrement] = policyVersion.split('.');
            return (Number(consentVersionYear) + Number(consentVersionIncrement) <
                Number(versionYear) + Number(versionIncrement));
        }
        return false;
    }
    /**
     * Always rendered. Not always visible
     */
    render() {
        const { heading, paragraph, anchor, paragraphPostfix, noConsentLabel, consentLabel } = content;
        const { visible, href, onClickNoConsent, onClickConsent, broadcastDismiss } = this;
        return html `
            <zz-drawer anchor="block-end" ?visible=${visible} prevent-user-dismiss @dismiss=${() => broadcastDismiss()}>
                <div class="container">
                    <header class="header">
                        <zz-text tag="h3">${heading}</zz-text>
                    </header>
                    <section>
                        <zz-text tag="p">
                            ${paragraph}
                            <zz-button inline variant="text" href=${href}>${anchor.title}</zz-button>
                            ${paragraphPostfix}
                        </zz-text>
                    </section>
                    <footer class="footer">
                        <zz-button variant="ghost" @click="${onClickNoConsent}">${noConsentLabel}</zz-button>
                        <zz-button variant="secondary" @click=${onClickConsent}>${consentLabel}</zz-button>
                    </footer>
                </div>
            </zz-drawer>
        `;
    }
}
__decorate([
    property({ type: Boolean, reflect: true })
], CookieBanner.prototype, "visible", void 0);
__decorate([
    property({ type: String })
], CookieBanner.prototype, "href", void 0);
